import CentredLoader from "components/inputs/ingestion/CentredLoader";
import DataValidation from "components/inputs/ingestion/DataValidation";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as tablesService from "services/tablesService";
import * as premDeductActions from "store/actions/input/exposure/premDeductActions";

export const PremiumDeductiblesTransformer = ({
  fileId,
  origin,
  tableName,
  headerRows,
  mapping,
  substitutions,
  rowDeletions,
  filename,
  tablesList,
  onConversionComplete,
  setDateConversionType,
  dateConversionTypeList,
  onSubstitutionsChanged,
  onRowDeletionsChanged,
}) => {
  const [columnMappings, setColumnMappings] = useState({});
  const [progressMessage, setProgressMessage] = useState("");

  useEffect(() => {
    const colMap = {};
    if (mapping) {
      for (const index in mapping) {
        const entry = mapping[index];
        colMap[entry.outputName] = entry.inputColumn;
      }
      setColumnMappings(colMap);
    }
  }, [mapping, setColumnMappings]);

  const { data: headings } = tablesService.useGetHeaderRowQuery(
    {
      fileId,
      tableName,
      skipRows: origin?.row ?? 0,
      skipColumns: origin?.column ?? 0,
      headerRows,
    },
    { skip: !fileId || !tableName }
  );

  const {
    data: mappingTable,
    status: mappingTableStatus,
  } = tablesService.useGetMappingQuery(
    {
      mappingName: "premium_deductibles",
      fileId,
      columnMappings,
    },
    {
      skip: !fileId,
    }
  );

  const {
    data: conversionErrors,
    status: conversionErrorsStatus,
  } = tablesService.useGetConversionErrorsQuery(
    {
      fileId,
      tableName,
      skipColumns: origin?.column ?? 0,
      skipRows: origin?.row ?? 0,
      headerRows,
      skipBlankRows: true,
      mapping: mappingTable,
    },
    { skip: mappingTableStatus !== "fulfilled" || !fileId || !tableName }
  );

  const hasConversionErrors =
    conversionErrorsStatus === "fulfilled" && conversionErrors?.errors?.length;

  const {
    data: processingResponse,
    status: processingResponseStatus,
  } = tablesService.useDoConversionQuery(
    {
      fileId,
      tableName,
      skipColumns: origin?.column ?? 0,
      skipRows: origin?.row ?? 0,
      headerRows,
      skipBlankRows: true,
      mapping: mappingTable,
      progressNotification: (timing) => {
        if (timing?.rowsProcessed) {
          if (timing.percentageComplete === 100) {
            setProgressMessage("Process complete, Saving...");
          } else {
            setProgressMessage(
              `${timing.rowsProcessed} rows processed ` +
                `${timing.percentageComplete}% estimated ` +
                `${timing.remainingSeconds} seconds left`
            );
          }
        } else {
          setProgressMessage("initialising...");
        }
      },
    },
    {
      skip:
        mappingTableStatus !== "fulfilled" ||
        hasConversionErrors ||
        !fileId ||
        !tableName,
    }
  );

  const dispatch = useDispatch();
  const [createdFile, setCreatedFile] = useState(null);
  useEffect(() => {
    if (
      processingResponseStatus === "fulfilled" &&
      processingResponse.createdFile &&
      createdFile !== processingResponse.createdFile
    ) {
      setCreatedFile(processingResponse.createdFile);
      dispatch(
        premDeductActions.transformCompleted(processingResponse.createdFile)
      );
      onConversionComplete();
    }
  }, [
    createdFile,
    setCreatedFile,
    processingResponse,
    processingResponseStatus,
    dispatch,
    fileId,
    tableName,
    filename,
    tablesList,
    onConversionComplete,
  ]);

  if (
    processingResponseStatus === "rejected" ||
    conversionErrorsStatus === "rejected"
  ) {
    return <>{"Error - failed to load file"}</>;
  } else if (conversionErrorsStatus !== "fulfilled") {
    return <CentredLoader label={"Validating data " + progressMessage} />;
  } else if (hasConversionErrors) {
    return (
      <DataValidation
        conversionErrors={conversionErrors}
        headings={headings}
        origin={origin}
        substitutions={substitutions}
        rowDeletions={rowDeletions}
        setDateConversionType={setDateConversionType}
        dateConversionTypeList={dateConversionTypeList}
        onReProcess={() => {}}
        onSubstitutionsChanged={onSubstitutionsChanged}
        onRowDeletionsChanged={onRowDeletionsChanged}
      />
    );
  } else {
    return <CentredLoader label={"Validating data " + progressMessage} />;
  }
};

export default PremiumDeductiblesTransformer;
